import { hourMs, minuteMs, secondMs } from "./timeConstants.js";

/**
 * Formats a duration in milliseconds as a localized whole-unit string, picking hours
 * when the duration is >= 1h and rounding to the nearest whole hour, otherwise picking
 * minutes and rounding to the nearest whole minute. Unit names + plural rules come
 * from CLDR via `Intl.NumberFormat`. Falls back to en-CA on an unrecognised locale.
 *
 * Examples (en-CA): `2 hours`, `1 hour`, `45 minutes`, `1 minute`.
 *
 * Intentionally narrow: only emits hours or minutes, only as whole numbers. For
 * other duration shapes (HH:MM:SS, "2h 30m", relative time, etc.) write a sibling
 * helper rather than overloading this one.
 */
export function formatRoundedHoursOrMinutes(ms: number, locale: string): string {
    let useHours = ms >= hourMs;
    let value = useHours ? Math.round(ms / hourMs) : Math.round(ms / minuteMs);

    if (!useHours && value >= 60) {
        useHours = true;
        value = Math.round(ms / hourMs);
    } else if (!useHours && value === 0 && ms > 0) {
        value = 1;
    }

    const unit = useHours ? "hour" : "minute";
    try {
        return new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "long" }).format(value);
    } catch {
        return new Intl.NumberFormat("en-CA", { style: "unit", unit, unitDisplay: "long" }).format(value);
    }
}

/**
 * Formats a duration in milliseconds as localized hours+minutes without dropping one
 * side. Examples (en-CA): `1 hour and 5 minutes`, `2 hours`, `45 minutes`.
 *
 * This avoids contradictory displays when related values are rendered separately
 * (observed/threshold/exceeded) by preserving both units from the same rounded
 * total-minute basis.
 */
export function formatHoursAndMinutes(ms: number, locale: string): string {
    const totalMinutes = Math.max(0, Math.round(ms / minuteMs));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    try {
        const formatUnit = (value: number, unit: "hour" | "minute") =>
            new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "long" }).format(value);

        if (hours === 0) return formatUnit(minutes, "minute");
        if (minutes === 0) return formatUnit(hours, "hour");
        return `${formatUnit(hours, "hour")} ${formatUnit(minutes, "minute")}`;
    } catch {
        const fallbackLocale = "en-CA";
        const formatUnit = (value: number, unit: "hour" | "minute") =>
            new Intl.NumberFormat(fallbackLocale, { style: "unit", unit, unitDisplay: "long" }).format(value);

        if (hours === 0) return formatUnit(minutes, "minute");
        if (minutes === 0) return formatUnit(hours, "hour");
        return `${formatUnit(hours, "hour")} ${formatUnit(minutes, "minute")}`;
    }
}

/**
 * Formats a duration in milliseconds as localized hours, minutes and seconds, omitting zero units.
 * Callers round `ms` to the precision they want shown. Examples (en-CA): `1 minute 30 seconds`, `45 seconds`, `2 hours`.
 */
export function formatHoursMinutesAndSeconds(ms: number, locale: string): string {
    const parts = [
        { value: Math.floor(ms / hourMs), unit: "hour" },
        { value: Math.floor((ms % hourMs) / minuteMs), unit: "minute" },
        { value: Math.floor((ms % minuteMs) / secondMs), unit: "second" },
    ].filter(({ value }) => value > 0);
    const format = (lng: string) =>
        parts
            .map(({ value, unit }) =>
                new Intl.NumberFormat(lng, { style: "unit", unit, unitDisplay: "long" }).format(value),
            )
            .join(" ");
    try {
        return format(locale);
    } catch {
        return format("en-CA");
    }
}
